/**
 * @module app.core
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { openGlobalModal, IModalData, IDialogOptions } from 'app/shared/elements/ess-global-modal';
import { RouteConfig } from 'aurelia-router';
import * as Core from 'app/core/models/essette-api';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { guid } from 'app/shared/lib/guid';
import { ImportsService } from 'app/core/services/imports-service';
import { Toaster } from 'app/shared/lib/toaster';
import { apiCall } from 'app/shared/lib/api/api';

interface IStagedRecordsSummaryModelData {
    importSection: Core.PolicySection;
}

@autoinject()
export class StagedRecordsSummary extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    protected systemCode: string | null = null;

    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    protected stagedJobId: string | null = null;

    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    protected tenantKey: string | null = null;

    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    protected tableName: string | null = null;

    @bindable.objectNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    protected tableFilter: Core.PolicySectionTableFilterContainer | null = null;

    private stagedPendingRecordColumns: ReadonlyArray<Core.Column> | null = null;
    private stagedPendingRecordRows: ReadonlyArray<Core.ImportTableRow> | null = null;
    private stagedRemovalRecordColumns: ReadonlyArray<Core.Column> | null = null;
    private stagedRemovalRecordRows: ReadonlyArray<Core.ImportTableRow> | null = null;
    protected pendingResultCount: number = 0;
    protected removalResultCount: number = 0;
    protected pendingPageSize: number = 50;
    protected removalPageSize: number = 50;
    protected importSection: Core.PolicySection | null = null;

    ////****************** Element References ***************************************////
    protected $pendingPager!: EssPagerCustomElement;
    protected $removalPager!: EssPagerCustomElement;
    protected id: guid = guid();

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: ImportsService,
        private readonly toaster: Toaster,
        ...rest: any[]) {

        super(...rest);
    }

    protected bind(): void {
        this.reload();
    }

    protected async activate(model: IStagedRecordsSummaryModelData, routeConfig: RouteConfig): Promise<void> {
        if(!model) { return; }

        if(model.importSection) {
            this.importSection = model.importSection;
        }

        if(this.importSection?.systemCode && this.importSection?.stagedJobIdentifier && this.importSection?.table?.name &&
            this.importSection?.tenant?.key) {
            this.systemCode = this.importSection.systemCode;
            this.stagedJobId = this.importSection.stagedJobIdentifier;
            this.tableName = this.importSection.table.name;
            this.tenantKey = this.importSection.tenant.key;
        }

        this.tableFilter = this.importSection?.table?.filter || null;
    }


    ////****************** Events ***************************************************////
    public async reload(): Promise<void> {
        await Promise.all([
            this.loadPendingImports(false),
            this.loadPendingImports(true)
        ]);
    }

    ////****************** Functions ************************************************////
    protected buildStagedRecordsTable(stagedForRemoval: boolean): Core.PolicySection {
        let policySection: Core.PolicySection = {
            systemCode: this.systemCode,
            stagedJobIdentifier: this.stagedJobId,
            tenant: { key: this.tenantKey },
            table: { name: this.tableName },
            stagedForRemoval: stagedForRemoval,
            paging: {
                top: stagedForRemoval ? this.removalPageSize : this.pendingPageSize,
                skip: stagedForRemoval ? (this.$removalPager?.page || 0) * this.pendingPageSize : (this.$pendingPager?.page || 0) * this.pendingPageSize, // calculates number of records to skip based on current page
            }
        };

        if(policySection.table && !!this.tableFilter?.field && !!this.tableFilter?.value) {
            policySection.table.filter = {
                field: this.tableFilter.field,
                value: this.tableFilter.value
            }
        }

        return policySection;
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadPendingImports(stagedForRemoval: boolean): Promise<void> {
        const queryParams: Core.PolicySection = this.buildStagedRecordsTable(stagedForRemoval);
        let isSuccessful = false;
        let errorMessage: string = "";
        try {
            this.state.beginLoad();
            const response = await this.service.loadPendingImports(queryParams);
            isSuccessful = response.isSuccessful;

            if(stagedForRemoval) {
                const removalRecordsTable = response.result?.table;
                this.stagedRemovalRecordColumns = removalRecordsTable?.columns;
                this.stagedRemovalRecordRows = removalRecordsTable?.rows;
                this.removalResultCount = removalRecordsTable?.$collections?.rows?.count || 0; // total number of records (unpaginated)
            }
            else {
                const pendingRecordsTable = response.result?.table;
                this.stagedPendingRecordColumns = pendingRecordsTable?.columns;
                this.stagedPendingRecordRows = pendingRecordsTable?.rows;
                this.pendingResultCount = pendingRecordsTable?.$collections?.rows?.count || 0; // total number of records (unpaginated)
            }
        } catch(error) {
            if(error.description) { errorMessage = error.description; }
            throw error;
        } finally {
            if(!isSuccessful) {
                const exception = (this.toaster.toast())
                    .title('Detail')
                    .class('ess-toast-error')
                    .icon('ui:icons.error')
                    .message(errorMessage)
                exception.show();
            }
            this.state.endLoad();
        }
    }
}

/**
 * Opens the import job staged records modal with the global dialog
 * @param importSection The job section information to use
 */
export function openStagedRecordsSummary(importSection: Core.PolicySection): void {
    if(!importSection || !importSection.stagedJobIdentifier || !importSection.systemCode ||
        !importSection.table?.name || !importSection.tenant?.key) {
        throw new Error('Policy Section is missing or invalid');
    }
    const categoryDisplay: string = importSection.category?.$display ? importSection.category?.$display : "Unknown Category";
    const sectionDisplay: string = importSection.$display ? importSection.$display : "Unknown Section";

    const sectionTitle = `${categoryDisplay}: ${sectionDisplay}`;
    const dialogOptions: IDialogOptions = {
        width: '10',
        height: '10',
        helpTopic: 'core.imports.job.staged',
        title: {
            key: 'core:routes.imports.staged.$title',
            replace: { display: sectionTitle }
        },
        print: true,
        popout: {
            route: `core.imports.job.staged`,
            params: {
                systemCode: importSection.systemCode,
                stagedJobId: importSection.stagedJobIdentifier,
                tenantKey: importSection.tenant?.key,
                tableName: importSection.table?.name,
                tableFilter: importSection.table?.filter,
                sectionTitle
            }
        }
    };
    const modelData: IStagedRecordsSummaryModelData = {
        importSection: importSection
    };

    const modalData: IModalData = {
        componentId: 'app/core/components/summary/job/staged-records-summary',
        modelData: modelData,
        dialogOptions: dialogOptions
    };

    openGlobalModal(modalData);
}




